// The immutable context bundle a report pins (ADR-0173 fase 2, PR-1).
//
// `context_versions.bundle` is what ai-svc reads — never the live rule
// tables. Version 2 of the shape (v1 = criteria + capture guides only,
// fase 1c reads those straight from the pinned template instead). The
// bundle carries, per rule version, the text by value, so archiving or
// redacting a rule later cannot change what an open acta is judged with.
//
// `content_sha256` is computed over the ORDERED list of
// (ruleVersionId, textSha256) — never over the texts — so redacting a text
// to the `[REDACTADO]` sentinel (fase 2 PR-11) leaves the frozen hash
// intact while the row records which rule versions were redacted.
// `@noble/hashes`, not `node:crypto`: this package is bundled into the
// dashboard and the PWA (see ids.ts for the same rule on randomUUID), and
// a `node:` builtin in a browser bundle fails `vite build` outright.
import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex, utf8ToBytes } from "@noble/hashes/utils.js";
import { z } from "zod";
import { ruleParamSchema, ruleVerificationSchema } from "./context-rule.js";
import { ruleResponsibleSchema } from "./context-source.js";
export const CONTEXT_BUNDLE_SCHEMA_VERSION = 2;
export const bundleRuleSchema = z.object({
    ruleId: z.string().uuid(),
    ruleVersionId: z.string().uuid(),
    sourceId: z.string().uuid(),
    /** Client item codes the rule attaches to (`1.1`). */
    itemCodes: z.array(z.string().min(1).max(32)).min(1),
    text: z.string().min(1).max(600),
    textSha256: z.string().regex(/^[0-9a-f]{64}$/),
    responsible: z.union([ruleResponsibleSchema, z.literal("no_especificado")]),
    verification: ruleVerificationSchema,
    params: z.array(ruleParamSchema).max(6),
    precedenceRank: z.number().int().min(1),
});
export const contextBundleV2Schema = z.object({
    schemaVersion: z.literal(CONTEXT_BUNDLE_SCHEMA_VERSION),
    organizationId: z.string().uuid(),
    brandId: z.string().uuid(),
    /** The brand's single active template when there is exactly one; null
     *  when the brand runs several (the codes then resolve across all of
     *  them — `bySection` is what ai-svc reads, never the template). */
    checklistTemplateId: z.string().uuid().nullable(),
    rules: z.array(bundleRuleSchema),
    /** inspection_area_id → rule indexes into `rules`, precomputed at build
     *  time so ai-svc never scans the whole bundle per bloque (§15). */
    bySection: z.record(z.string().uuid(), z.array(z.number().int().nonnegative())),
    /** item client code → rule indexes. */
    byItemCode: z.record(z.string(), z.array(z.number().int().nonnegative())),
});
export function sha256Hex(text) {
    return bytesToHex(sha256(utf8ToBytes(text)));
}
/** Stable hash of the bundle's identity: ordered (ruleVersionId, textSha256). */
export function bundleContentSha256(rules) {
    const ordered = [...rules]
        .sort((a, b) => a.ruleVersionId.localeCompare(b.ruleVersionId))
        .map((r) => `${r.ruleVersionId}:${r.textSha256}`)
        .join("\n");
    return sha256Hex(ordered);
}
/** Builds the indexes from the rule list and the item catalog (item code →
 *  section id, or several when the brand runs more than one template and
 *  the same client code lives in each). Pure. */
export function indexBundleRules(rules, sectionByItemCode) {
    const bySection = {};
    const byItemCode = {};
    rules.forEach((rule, i) => {
        for (const code of rule.itemCodes) {
            (byItemCode[code] ??= []).push(i);
            const found = sectionByItemCode.get(code);
            const sections = found === undefined ? [] : typeof found === "string" ? [found] : found;
            for (const section of sections)
                (bySection[section] ??= []).push(i);
        }
    });
    for (const k of Object.keys(bySection))
        bySection[k] = [...new Set(bySection[k])];
    return { bySection, byItemCode };
}
/** The single-active-value-per-slot assertion (exploración §6): two ACTIVE
 *  rules that set the same (item code, param name, scope) from different
 *  sources is a conflict the activation must refuse, not something the
 *  prompt resolves. Returns the conflicting slots; empty = ok. */
export function findSlotConflicts(rules) {
    const slots = new Map();
    for (const rule of rules) {
        for (const code of rule.itemCodes) {
            for (const p of rule.params) {
                const key = `${code}\u0000${p.name}\u0000${p.scope ?? ""}`;
                (slots.get(key) ?? slots.set(key, new Set()).get(key)).add(rule.ruleVersionId);
            }
        }
    }
    const out = [];
    for (const [key, ids] of slots) {
        if (ids.size < 2)
            continue;
        const [itemCode, param, scope] = key.split("\u0000");
        out.push({
            itemCode,
            param,
            scope: scope === "" ? null : scope,
            ruleVersionIds: [...ids].sort(),
        });
    }
    return out;
}
//# sourceMappingURL=context-bundle.js.map